import { supabase } from '../supabase';

// Void + the customer-facing void notification, extracted from
// invoiceService.js to keep that file under the 500-line cap. Both are
// re-exported from invoiceService, so every existing
// `invoiceService.voidInvoice(...)` / `sendVoidNotification(...)` call site is
// unchanged.

/**
 * Void a sent or paid invoice. The void_invoice() RPC atomically validates
 * state, writes void_reason / voided_at / voided_by, and emits
 * audit_events invoice:voided (RD-AUD-013 M-05). Do NOT also write a client
 * audit row — that double-emits (auditSingleEmission).
 *
 * @param {string} invoiceId
 * @param {{ reason: string, reasonOther?: string }} opts
 */
export const voidInvoice = async (invoiceId, { reason, reasonOther } = {}) => {
  const { data, error } = await supabase.rpc('void_invoice', {
    p_invoice_id: invoiceId,
    p_reason: reason,
    p_reason_other: reasonOther || null,
  });
  return { data, error };
};

/**
 * Notify the customer that an invoice they previously received has been
 * voided. Called post-void from useInvoiceCrud.executeVoidInvoice when the
 * pre-void status was 'sent' or 'paid' (i.e. the customer has the original
 * invoice in their inbox). No-op for drafts (customer never saw it) and
 * sending state (rare race).
 *
 * Failure is non-fatal — the void itself already succeeded; surface the
 * error to the admin but don't roll back. Returns { ok, error }.
 */
export const sendVoidNotification = async (invoiceId, { recipientEmail, message } = {}) => {
  const { data, error } = await supabase.functions.invoke('send-void-notification', {
    body: {
      invoice_id: invoiceId,
      recipient_email: recipientEmail || undefined,
      message: message || undefined,
    },
  });
  return { data, error };
};
